import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Plus, Trash2, ArrowLeft, FileText, ClipboardCheck, Search, CalendarDays, ChevronRight, AlertCircle, Eye, ListFilter, Clock, Edit, Vote, BarChart3, Check as CheckIcon, Send, Loader2 } from 'lucide-react';
import { API, fmtDate } from '@/lib/helpers';
import TipTapEditor from './admin/TipTapEditor';

const STATUS_MAP = {
  agendada: { label: 'Agendada', color: 'bg-violet-100 text-violet-700', border: 'border-l-violet-500' },
  em_andamento: { label: 'Em Andamento', color: 'bg-amber-100 text-amber-700', border: 'border-l-amber-500' },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700', border: 'border-l-green-500' },
};

const FILTROS_REUNIAO = [
  { key: 'todas', label: 'Todas', icon: 'bg-violet-100', iconColor: 'text-violet-600' },
  { key: 'agendada', label: 'Agendadas', icon: 'bg-indigo-100', iconColor: 'text-indigo-600' },
  { key: 'em_andamento', label: 'Em Andamento', icon: 'bg-amber-100', iconColor: 'text-amber-600' },
  { key: 'concluida', label: 'Concluídas', icon: 'bg-green-100', iconColor: 'text-green-600' },
];

export default function AuxiliarReunioesTab({ votacoesEnabled = false }) {
  const [reunioes, setReunioes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('todas');
  const [selectedReuniao, setSelectedReuniao] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Pauta dialog
  const [pautaDialog, setPautaDialog] = useState(false);
  const [editingPauta, setEditingPauta] = useState(null);
  const [pautaText, setPautaText] = useState('');
  const [savingPauta, setSavingPauta] = useState(false);

  // Votações
  const [reuniaoVotacoes, setReuniaoVotacoes] = useState([]);
  const [votacaoAtual, setVotacaoAtual] = useState(null);
  const [editingResposta, setEditingResposta] = useState(false);
  const [respostaTexto, setRespostaTexto] = useState('');
  const [opcoesSelecionadas, setOpcoesSelecionadas] = useState([]);
  const [savingVoto, setSavingVoto] = useState(false);
  const [resultadoOpen, setResultadoOpen] = useState(null);
  const [resultadoData, setResultadoData] = useState(null);

  const fetchReunioes = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reunioes`, { credentials: 'include' });
      if (r.ok) setReunioes(await r.json());
    } catch (err) { console.warn('Falha ao carregar reuniões do auxiliar', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReunioes(); }, [fetchReunioes]);

  const fetchDetail = useCallback(async (reuniaoId) => {
    setLoadingDetail(true);
    try {
      const r = await fetch(`${API}/reunioes/${reuniaoId}`, { credentials: 'include' });
      if (r.ok) setDetail(await r.json());
    } catch { toast.error('Erro ao carregar reunião'); }
    finally { setLoadingDetail(false); }
  }, []);

  const fetchReuniaoVotacoes = useCallback(async (reuniaoId) => {
    try {
      const r = await fetch(`${API}/reunioes/${reuniaoId}/votacoes`, { credentials: 'include' });
      if (r.ok) setReuniaoVotacoes(await r.json());
    } catch (err) { console.warn('Falha ao carregar votações do auxiliar', err); }
  }, []);

  const openDetail = (reuniao) => { setSelectedReuniao(reuniao.reuniao_id); fetchDetail(reuniao.reuniao_id); fetchReuniaoVotacoes(reuniao.reuniao_id); };
  const backToList = () => { setSelectedReuniao(null); setDetail(null); setReuniaoVotacoes([]); fetchReunioes(); };

  const handleAddPauta = async () => {
    if (!pautaText.trim()) { toast.error('Texto da pauta é obrigatório'); return; }
    setSavingPauta(true);
    try {
      const url = editingPauta
        ? `${API}/reunioes/${detail.reuniao_id}/pautas/${editingPauta.pauta_id}`
        : `${API}/reunioes/${detail.reuniao_id}/pautas`;
      const r = await fetch(url, {
        method: editingPauta ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ texto: pautaText.trim() }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Erro'); }
      toast.success(editingPauta ? 'Pauta atualizada!' : 'Pauta adicionada!');
      setPautaDialog(false); setPautaText(''); setEditingPauta(null);
      fetchDetail(detail.reuniao_id);
    } catch (err) { toast.error(err.message); }
    finally { setSavingPauta(false); }
  };

  const handleDeletePauta = async (pautaId) => {
    try {
      const r = await fetch(`${API}/reunioes/${detail.reuniao_id}/pautas/${pautaId}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Erro'); }
      toast.success('Pauta removida!');
      fetchDetail(detail.reuniao_id);
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleTask = async (tarefa) => {
    try {
      const r = await fetch(`${API}/reunioes/${detail.reuniao_id}/tarefas/${tarefa.tarefa_id}/toggle`, { method: 'PUT', credentials: 'include' });
      if (!r.ok) throw new Error();
      fetchDetail(detail.reuniao_id);
    } catch { toast.error('Erro ao atualizar tarefa'); }
  };

  const openVotar = (v) => {
    setVotacaoAtual(v); setRespostaTexto(''); setOpcoesSelecionadas([]); setEditingResposta(false);
  };

  const toggleOpcao = (idx, permitirMultiplas) => {
    if (permitirMultiplas) {
      setOpcoesSelecionadas(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
    } else {
      setOpcoesSelecionadas([idx]);
    }
  };

  const handleVotar = async () => {
    if (!votacaoAtual) return;
    if (votacaoAtual.tipo === 'texto' && !respostaTexto.trim()) { toast.error('Por favor, escreva sua resposta.'); return; }
    if (votacaoAtual.tipo !== 'texto' && opcoesSelecionadas.length === 0) { toast.error('Selecione pelo menos uma opção.'); return; }
    setSavingVoto(true);
    try {
      const body = votacaoAtual.tipo === 'texto' ? { texto: respostaTexto } : { opcoes_selecionadas: opcoesSelecionadas };
      const res = await fetch(`${API}/votacoes/${votacaoAtual.votacao_id}/responder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Erro'); }
      toast.success('Voto registrado!');
      setVotacaoAtual(null);
      fetchReuniaoVotacoes(detail.reuniao_id);
    } catch (err) { toast.error(err.message); } finally { setSavingVoto(false); }
  };

  const handleVerResultados = async (v) => {
    setResultadoOpen(v);
    setResultadoData(null);
    try {
      const res = await fetch(`${API}/votacoes/${v.votacao_id}/resultados`, { credentials: 'include' });
      if (res.ok) { setResultadoData(await res.json()); }
      else {
        const text = await res.text();
        let msg = 'Erro ao carregar resultados';
        try { msg = JSON.parse(text).detail || msg; } catch (err) { console.warn('Falha ao ler erro dos resultados', err); }
        toast.error(msg);
        setResultadoOpen(null);
      }
    } catch { toast.error('Erro ao carregar resultados'); setResultadoOpen(null); }
  };

  // Filter
  const counts = {
    todas: reunioes.length,
    agendada: reunioes.filter(r => r.status === 'agendada').length,
    em_andamento: reunioes.filter(r => r.status === 'em_andamento').length,
    concluida: reunioes.filter(r => r.status === 'concluida').length,
  };

  const filtered = reunioes.filter(r => {
    if (filter !== 'todas' && r.status !== filter) return false;
    if (search && !r.titulo.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // ========== DETAIL VIEW ==========
  if (selectedReuniao) {
    if (loadingDetail || !detail) {
      return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div></div>;
    }
    const st = STATUS_MAP[detail.status] || STATUS_MAP.agendada;
    const canAddPauta = detail.status !== 'concluida';
    const showAta = detail.status === 'concluida' && detail.ata_conteudo;

    return (
      <div data-testid="auxiliar-reuniao-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={backToList} className="bg-white w-fit" data-testid="aux-back-to-list"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{detail.titulo}</h2>
              <Badge className={st.color}>{st.label}</Badge>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-400 mt-1 flex-wrap">
              <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(detail.data)}{detail.hora ? ` às ${detail.hora}` : ''}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* LEFT: Ata (read-only) */}
          {showAta && (
            <Card className="border-0 shadow-sm overflow-hidden lg:col-span-3 flex flex-col" data-testid="aux-ata-section">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900">Ata da Reunião</span>
                <Badge className="bg-slate-100 text-slate-500 text-[10px]">Somente leitura</Badge>
              </div>
              <CardContent className="p-0 flex-1">
                <TipTapEditor content={detail.ata_conteudo || ''} readOnly={true} testId="auxiliar-ata-richtext-editor" />
              </CardContent>
            </Card>
          )}

          {!showAta && (
            <div className="lg:col-span-3">
              <div className="bg-blue-50 rounded-lg border border-blue-100 p-4 text-sm text-blue-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>A ata da reunião ficará disponível após a conclusão da reunião.</span>
              </div>
            </div>
          )}

          {/* RIGHT: Pauta + Tarefas */}
          <div className="flex flex-col gap-4 lg:col-span-2">
            {/* PAUTA */}
            <Card className="border-0 shadow-sm overflow-hidden" data-testid="aux-pauta-section">
              <div className="bg-violet-50 px-4 py-3 border-b border-violet-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-violet-600" /><span className="text-sm font-semibold text-violet-900">Pauta</span></div>
                <span className="text-xs text-violet-500">{detail.pautas?.length || 0} itens</span>
              </div>
              <div>
                {(detail.pautas || []).length > 0 ? (
                  <div className="p-3 flex flex-col gap-3">
                    {(detail.pautas || []).map((p, idx) => {
                      const isMine = p.created_by_role === 'auxiliar';
                      return (
                        <div key={p.pauta_id} className={`flex items-center gap-2 p-2 rounded-lg group bg-slate-50`} data-testid={`aux-pauta-${p.pauta_id}`}>
                          <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800">{p.texto}</p>
                            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
                              {p.created_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(p.created_at)}</span>}
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                <span>{p.created_by_name}</span>
                              </span>
                            </div>
                          </div>
                          {canAddPauta && isMine && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                              <button onClick={() => { setEditingPauta(p); setPautaText(p.texto); setPautaDialog(true); }}
                                className="p-1 hover:bg-slate-200 rounded" data-testid={`aux-edit-pauta-${p.pauta_id}`}>
                                <Edit className="w-3.5 h-3.5 text-slate-400" />
                              </button>
                              <button onClick={() => handleDeletePauta(p.pauta_id)}
                                className="p-1 hover:bg-slate-200 rounded" data-testid={`aux-delete-pauta-${p.pauta_id}`}>
                                <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-6 text-center"><FileText className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Nenhuma pauta</p></div>
                )}
              </div>
              {canAddPauta && (
                <div className="p-3 border-t border-slate-100">
                  <Button variant="outline" size="sm" onClick={() => { setPautaText(''); setEditingPauta(null); setPautaDialog(true); }}
                    className="w-full border-dashed border-violet-200 text-violet-600 hover:bg-violet-50" data-testid="aux-add-pauta-btn">
                    <Plus className="w-4 h-4 mr-1" />Adicionar Pauta
                  </Button>
                </div>
              )}
            </Card>

            {/* TAREFAS */}
            <Card className="border-0 shadow-sm overflow-hidden" data-testid="aux-tarefas-section">
              <div className="bg-green-50 px-4 py-3 border-b border-green-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-green-600" /><span className="text-sm font-semibold text-green-900">Tarefas</span></div>
                <span className="text-xs text-green-600">{(detail.tarefas || []).filter(t => t.concluida).length}/{(detail.tarefas || []).length} concluídas</span>
              </div>
              <div>
                {(detail.tarefas || []).length === 0 ? (
                  <div className="p-6 text-center"><ClipboardCheck className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Nenhuma tarefa</p></div>
                ) : (
                  <div className="p-3 flex flex-col gap-3">
                    {(detail.tarefas || []).map(t => (
                      <div key={t.tarefa_id}
                        className="flex items-center gap-2 p-2 rounded-lg bg-slate-50"
                        data-testid={`aux-tarefa-${t.tarefa_id}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0 ${t.concluida ? 'bg-green-500' : ''}`} style={t.concluida ? undefined : { backgroundColor: '#939393' }} title={t.atribuido_a_name}>
                          {t.concluida ? <CheckIcon className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-800">{t.descricao}</p>
                          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400">
                            {t.prazo && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{fmtDate(t.prazo)}</span>}
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{t.atribuido_a_name}</span>
                            {t.concluida
                              ? <Badge className="bg-green-100 text-green-700 text-[10px]">Concluída</Badge>
                              : <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pendente</Badge>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            {/* VOTAÇÕES */}
            {votacoesEnabled && (
            <Card className="border-0 shadow-sm overflow-hidden" data-testid="aux-votacoes-section">
              <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-100 flex items-center justify-between">
                <div className="flex items-center gap-2"><Vote className="w-4 h-4 text-indigo-600" /><span className="text-sm font-semibold text-indigo-900">Votações</span></div>
                <span className="text-xs text-indigo-500">{reuniaoVotacoes.filter(v => v.status !== 'nao_enviada').length} disponíveis</span>
              </div>
              <div>
                {(() => {
                  const visibleVotacoes = reuniaoVotacoes.filter(v => v.status !== 'nao_enviada');
                  if (visibleVotacoes.length === 0) return (
                    <div className="p-6 text-center"><Vote className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Nenhuma votação disponível</p></div>
                  );
                  return (
                    <div className="p-3 flex flex-col gap-3">
                      {visibleVotacoes.map(v => {
                        const jaVotou = !!v.minha_resposta;
                        const isEncerrada = v.status === 'encerrada';
                        return (
                          <div key={v.votacao_id} className={`flex items-center gap-2 p-2 rounded-lg bg-slate-50`} data-testid={`aux-votacao-${v.votacao_id}`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isEncerrada ? 'bg-red-100' : 'bg-indigo-100'}`}>
                              <Vote className={`w-3.5 h-3.5 ${isEncerrada ? 'text-red-600' : 'text-indigo-600'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-800">{v.pergunta}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {jaVotou && <span className="text-[11px] text-slate-500">Votado</span>}
                                {isEncerrada && <Badge className="bg-red-100 text-red-700 text-[10px]">Encerrada</Badge>}
                                {!jaVotou && !isEncerrada && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Aberta</Badge>}
                                <span className="text-[11px] text-slate-400">{v.total_respostas || 0} voto{(v.total_respostas || 0) !== 1 ? 's' : ''}</span>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {!jaVotou && !isEncerrada && (
                                <Button variant="outline" size="sm" onClick={() => openVotar(v)} className="h-7 text-xs text-indigo-600 border-indigo-200" data-testid={`aux-votar-${v.votacao_id}`}>
                                  Votar
                                </Button>
                              )}
                              {(() => {
                                const vis = v.resultado_visibilidade || 'nunca';
                                const canSee = (vis === 'apos_votar' && (jaVotou || isEncerrada)) || (vis === 'apos_encerrar' && isEncerrada);
                                return canSee ? (
                                  <button onClick={() => handleVerResultados(v)} className="p-1 hover:bg-slate-200 rounded" data-testid={`aux-resultados-${v.votacao_id}`}>
                                    <BarChart3 className="w-4 h-4 text-indigo-500" />
                                  </button>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </Card>
            )}
          </div>
        </div>

        {/* VOTAR DIALOG */}
        <Dialog open={!!votacaoAtual} onOpenChange={() => { setVotacaoAtual(null); setEditingResposta(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingResposta ? 'Editar Resposta' : 'Responder Votação'}</DialogTitle>
            </DialogHeader>
            {votacaoAtual && (
              <div className="space-y-4">
                <div className="border border-slate-200 rounded-xl p-4">
                  <p className="font-semibold text-sm text-slate-900 mb-4">{votacaoAtual.pergunta}</p>
                  {votacaoAtual.tipo === 'texto' ? (
                    <Textarea value={respostaTexto} onChange={e => setRespostaTexto(e.target.value)} className="min-h-[80px] bg-white border-slate-200" data-testid="aux-voto-texto" />
                  ) : (
                    <div>
                      {(votacaoAtual.opcoes || []).map((op, i) => {
                        const selected = opcoesSelecionadas.includes(i);
                        return (
                          <button key={i} onClick={() => toggleOpcao(i, votacaoAtual.permitir_multiplas)}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-center gap-3 mt-2.5 first:mt-0 ${selected ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}
                            data-testid={`aux-voto-opcao-${i}`}>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'}`}>
                              {selected && <CheckIcon className="w-3 h-3 text-white" />}
                            </div>
                            <span className="text-sm text-slate-900">{op.texto}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setVotacaoAtual(null)}>Cancelar</Button>
                  <Button onClick={handleVotar} disabled={savingVoto} className="bg-indigo-600 hover:bg-indigo-700" data-testid="aux-confirmar-voto">
                    {savingVoto ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Aguarde...</> : <><Send className="w-4 h-4 mr-1" />{editingResposta ? 'Salvar alteração' : 'Enviar resposta'}</>}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* RESULTADOS DIALOG */}
        <Dialog open={!!resultadoOpen} onOpenChange={() => { setResultadoOpen(null); setResultadoData(null); }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Resultados</DialogTitle>
            </DialogHeader>
            {resultadoOpen && (
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-900 mb-4">{resultadoOpen.pergunta}</p>

                {!resultadoData && <p className="text-sm text-slate-400 text-center py-4">A carregar...</p>}

                {resultadoData && resultadoOpen.tipo === 'multipla_escolha' && (
                  <div className="space-y-4">
                    {resultadoData.votacao?.opcoes?.map((op, i) => {
                      const count = resultadoData.opcoes_contagem?.[i] || 0;
                      const total = resultadoData.total || 1;
                      const pct = Math.round((count / total) * 100);
                      const isCorreta = resultadoData.votacao?.marcar_correta && op.correta;
                      return (
                        <div key={i} className="mb-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs text-slate-700 font-medium">{op.texto}</p>
                              {isCorreta && <span className="text-[10px] bg-green-100 text-green-700 font-semibold px-1.5 py-0.5 rounded">Correta</span>}
                            </div>
                            <span className="text-xs text-slate-500">{count} / {pct}%</span>
                          </div>
                          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${isCorreta ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {resultadoData && resultadoOpen.tipo === 'texto' && (
                  <div className="space-y-2">
                    {(resultadoData.respostas || []).map((r, i) => (
                      <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-indigo-600">{r.user_name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800">{r.user_name}</p>
                          <p className="text-xs text-slate-600 mt-0.5">&ldquo;{r.texto}&rdquo;</p>
                        </div>
                      </div>
                    ))}
                    {(resultadoData.respostas || []).length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-2">Nenhuma resposta ainda.</p>
                    )}
                  </div>
                )}

                {resultadoData && resultadoOpen.tipo === 'multipla_escolha' && (resultadoData.respostas || []).length > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Respostas individuais</p>
                    <div className="space-y-2">
                      {(resultadoData.respostas || []).map((r, i) => (
                        <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                          <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-indigo-600">{r.user_name?.charAt(0)}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-800">{r.user_name}</p>
                            <p className="text-xs text-slate-600 mt-0.5">
                              &ldquo;{(r.opcoes_selecionadas || []).map(idx => resultadoData.votacao?.opcoes?.[idx]?.texto).filter(Boolean).join(', ')}&rdquo;
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {resultadoData && (
                  <p className="text-xs text-slate-500 pt-1 border-t border-slate-100">{resultadoData.total} {resultadoData.total === 1 ? 'resposta' : 'respostas'} no total</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* PAUTA DIALOG */}
        <Dialog open={pautaDialog} onOpenChange={v => { if (!v) { setEditingPauta(null); setPautaText(''); } setPautaDialog(v); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingPauta ? 'Editar Pauta' : 'Adicionar Pauta'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Pauta</Label><Textarea rows={3} value={pautaText} onChange={e => setPautaText(e.target.value)} data-testid="aux-pauta-input" /></div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setPautaDialog(false)}>Cancelar</Button>
                <Button onClick={handleAddPauta} disabled={savingPauta} className="bg-violet-600 hover:bg-violet-700" data-testid="aux-save-pauta-btn">{savingPauta ? 'Salvando...' : (editingPauta ? 'Salvar' : 'Adicionar')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========== LIST VIEW ==========
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="auxiliar-reunioes-list">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Reuniões</h2>
                  <Badge className="bg-violet-100 text-violet-700 text-sm font-bold">{counts.todas}</Badge>
                </div>
                <p className="text-sm text-slate-500">Filtre por status</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="aux-reunioes-menu">
              {FILTROS_REUNIAO.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`aux-filtro-reuniao-${f.key}`}
                  className={`w-full lg:w-full min-w-[140px] lg:min-w-0 text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-violet-600 bg-violet-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.icon}`}>
                      <ListFilter className={`w-4 h-4 ${f.iconColor}`} />
                    </div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-violet-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>{counts[f.key]}</Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content */}
      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FILTROS_REUNIAO.find(f => f.key === filter)?.label}</h2>
                <p className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'reunião' : 'reuniões'}</p>
              </div>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="aux-search-reunioes" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-0" data-testid="aux-reunioes-list">
              {loading ? (
                <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{search || filter !== 'todas' ? 'Nenhuma reunião encontrada' : 'Nenhuma reunião disponível'}</p>
                </div>
              ) : filtered.map(r => {
                const st = STATUS_MAP[r.status] || STATUS_MAP.agendada;
                return (
                  <div key={r.reuniao_id} className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 cursor-pointer transition-colors"
                    onClick={() => openDetail(r)} data-testid={`aux-reuniao-card-${r.reuniao_id}`}>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 font-medium">{r.titulo}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <CalendarDays className="w-3 h-3" />{fmtDate(r.data)}{r.hora ? ` às ${r.hora}` : ''}
                          </span>
                          <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
                          <span className="text-[10px] text-slate-400">{r.total_pautas} {r.total_pautas === 1 ? 'pauta' : 'pautas'}</span>
                          {(r.minhas_tarefas_pendentes || 0) > 0 && (
                            <span className="text-[10px] text-amber-500 font-medium">{r.minhas_tarefas_pendentes} tarefa{r.minhas_tarefas_pendentes > 1 ? 's' : ''} para mim</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 mt-1 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
